import { createContext, useEffect, useReducer } from 'react'
import { useDispatch } from 'react-redux'
import { useSnackbar } from 'notistack'
import type { FC, ReactNode } from 'react'
import PropTypes from 'prop-types'
import LogRocket from 'logrocket'
import { Auth0Client } from '@auth0/auth0-spa-js'
import { setContactSettings } from 'src/slices/accounts'
import { auth0Config } from '../config'
import type { User, UserJwts } from '../types/user'
import { HTTP } from '../lib/axios'
import { getAllContacts } from '../slices/contacts'
import { getChats } from '../slices/sms'
import { getFellowUsers } from '../slices/phone'
import { getMatters } from '../slices/matters'
import { isElectron } from '../utils/common'
import wait from '../utils/wait'

const electronAuth = isElectron()

let ipcRenderer = null

if (electronAuth) {
  ipcRenderer = window.require('electron').ipcRenderer
}

let auth0Client: Auth0Client | null = null

interface State {
  isInitialized: boolean
  isAuthenticated: boolean
  user: User | null
  tokens: UserJwts
  permissions: any
  numbers: any
  account: any
  statusMessages: Array<any>
}

export interface AuthContextValue extends State {
  platform: 'Auth0'
  loginWithPopup: (options?: any) => Promise<void>
  logout: () => void
}

interface AuthProviderProps {
  children: ReactNode
}

type InitializeAction = {
  type: 'INITIALIZE'
  payload: {
    isAuthenticated: boolean
    user: User | null
    tokens: any
    permissions: any
    numbers: any
    account: any
    statusMessages: Array<any>
  }
}

type LoginAction = {
  type: 'LOGIN'
  payload: {
    user: User
    tokens: any
    permissions: any
    numbers: any
    account: any
    statusMessages: Array<any>
  }
}

type LogoutAction = {
  type: 'LOGOUT'
}

type RegisterAction = {
  type: 'REGISTER'
}

type Action = InitializeAction | LoginAction | LogoutAction | RegisterAction

const initialState: State = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  tokens: null,
  permissions: null,
  numbers: null,
  account: null,
  statusMessages: []
}

const handlers: Record<string, (state: State, action: Action) => State> = {
  INITIALIZE: (state: State, action: InitializeAction): State => {
    const {
      isAuthenticated,
      user,
      tokens,
      permissions,
      numbers,
      account,
      statusMessages
    } = action.payload

    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
      tokens,
      permissions,
      numbers,
      account,
      statusMessages
    }
  },
  LOGIN: (state: State, action: LoginAction): State => {
    const { user, tokens, permissions, numbers, account, statusMessages } =
      action.payload

    return {
      ...state,
      isAuthenticated: true,
      user,
      tokens,
      permissions,
      numbers,
      account,
      statusMessages
    }
  },
  LOGOUT: (state: State): State => ({
    ...state,
    isAuthenticated: false,
    user: null,
    tokens: null,
    permissions: null,
    numbers: null,
    account: null,
    statusMessages: []
  })
}

const reducer = (state: State, action: Action): State =>
  handlers[action.type] ? handlers[action.type](state, action) : state

const AuthContext = createContext<AuthContextValue>({
  ...initialState,
  platform: 'Auth0',
  loginWithPopup: () => Promise.resolve(),
  logout: () => Promise.resolve()
})

export const AuthProvider: FC<AuthProviderProps> = (props) => {
  const { enqueueSnackbar } = useSnackbar()
  const reduxDispatch = useDispatch()
  const { children } = props
  const [state, dispatch] = useReducer(reducer, initialState)

  const logout = (): void => {
    if (!electronAuth) {
      auth0Client.logout({ returnTo: process.env.REACT_APP_BASE_URL })
      dispatch({
        type: 'LOGOUT'
      })
    } else {
      ipcRenderer.invoke('requestAction', { action: 'logout' }).then(() => {
        dispatch({
          type: 'LOGOUT'
        })
      })
    }
  }

  useEffect(() => {
    const initialize = async (): Promise<void> => {
      try {
        let isAuthenticated = false
        let token = ''
        if (electronAuth) {
          try {
            const result = await ipcRenderer.invoke('requestAction', {
              action: 'checkIsLoggedIn'
            })
            if (result !== 'failed') {
              isAuthenticated = true
              token = result
            }
          } catch (err) {
            console.error(err)
            enqueueSnackbar('Error encountered during login', {
              anchorOrigin: {
                horizontal: 'right',
                vertical: 'top'
              },
              variant: 'error'
            })
            logout()
          }
        } else {
          auth0Client = new Auth0Client({
            redirect_uri: window.location.origin,
            ...auth0Config
          })

          await auth0Client.checkSession()
          isAuthenticated = await auth0Client.isAuthenticated()
        }

        if (isAuthenticated) {
          // Here you should extract the complete user profile to make it
          // available in your entire app.
          // The auth state only provides basic information.

          if (!electronAuth) {
            const claims = await auth0Client.getIdTokenClaims()
            token = claims.__raw
          }

          try {
            const res = await HTTP.post(
              '/users/auth/identify',
              {},
              {
                headers: {
                  Authorization: `Bearer ${token}`
                }
              }
            )
            const { data } = res
            if (!data.data.user.australisId) {
              enqueueSnackbar(
                'You do not have permission to access this portal',
                {
                  anchorOrigin: {
                    horizontal: 'right',
                    vertical: 'bottom'
                  },
                  variant: 'error'
                }
              )
              logout()
            } else {
              const { permissionsInfo, account, user, statusMessages } =
                data.data
              if (process.env.NODE_ENV !== 'development') {
                LogRocket.identify(user.australisId, {
                  email: user.email,
                  australisId: user.australisId,
                  accountId: user.accountId
                })
              }
              const tokens = permissionsInfo.jwts
              const permissions = permissionsInfo.resources
              const numbers = { ...permissionsInfo.resources }
              for (const num in numbers) {
                if (!num.includes('+')) {
                  delete numbers[num]
                }
              }
              reduxDispatch(getChats(numbers, tokens))
              reduxDispatch(getAllContacts(token))
              reduxDispatch(getMatters(token, user.accountId, user.australisId))
              reduxDispatch(getFellowUsers(token))
              //   if (account.smsOnly === true) {
              //     reduxDispatch(getFellowUsers(token))
              //   }
              dispatch({
                type: 'INITIALIZE',
                payload: {
                  isAuthenticated: true,
                  user: {
                    auth0Token: token,
                    id: user.sub,
                    avatar: user.picture,
                    email: user.email,
                    name: `${data.data.user.firstName} ${data.data.user.lastName}`,
                    ...data.data.user
                  },
                  tokens,
                  permissions,
                  numbers,
                  account,
                  statusMessages
                }
              })
              reduxDispatch(
                setContactSettings(
                  account?.contactsData?.contactSettingsTemplates
                )
              )
            }
          } catch (error) {
            console.log(error)
            console.log(error.response)
            logout()
          }
        } else {
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated,
              user: null,
              tokens: null,
              permissions: null,
              numbers: null,
              account: null,
              statusMessages: []
            }
          })
        }
      } catch (err) {
        console.error(err)
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null,
            tokens: null,
            permissions: null,
            numbers: null,
            account: null,
            statusMessages: []
          }
        })
      }
    }

    initialize()
  }, [])

  const loginWithPopup = async (options): Promise<void> => {
    let isAuthenticated = false
    let token = ''

    if (electronAuth) {
      try {
        const result = await ipcRenderer.invoke('requestAction', {
          action: 'login'
        })
        if (result !== 'failed') {
          isAuthenticated = true
          token = result
        }
      } catch (err) {
        console.error(err)
        enqueueSnackbar('Error encountered during login', {
          anchorOrigin: {
            horizontal: 'right',
            vertical: 'top'
          },
          variant: 'error'
        })
        logout()
      }
    } else {
      await auth0Client.loginWithPopup(options)
      isAuthenticated = await auth0Client.isAuthenticated()
    }
    if (isAuthenticated) {
      // Here you should extract the complete user profile to make it available in your entire app.
      // The auth state only provides basic information.
      if (!electronAuth) {
        const claims = await auth0Client.getIdTokenClaims()
        token = claims.__raw
      }
      try {
        const res = await HTTP.post(
          '/users/auth/identify',
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        )
        const { data } = res
        if (!data.data.user.australisId) {
          enqueueSnackbar('You do not have permission to access this portal', {
            anchorOrigin: {
              horizontal: 'right',
              vertical: 'top'
            },
            variant: 'error'
          })
          logout()
        } else {
          const { permissionsInfo, account, user, statusMessages } = data.data
          if (process.env.NODE_ENV !== 'development') {
            LogRocket.identify(user.australisId, {
              email: user.email,
              australisId: user.australisId,
              accountId: user.accountId
            })
          }
          const tokens = permissionsInfo.jwts
          const permissions = permissionsInfo.resources
          const numbers = { ...permissionsInfo.resources }
          for (const num in numbers) {
            if (!num.includes('+')) {
              delete numbers[num]
            }
          }
          reduxDispatch(getChats(numbers, tokens))
          reduxDispatch(getAllContacts(token))
          reduxDispatch(getMatters(token, user.accountId, user.australisId))
          if (account.smsOnly === true || account.kazooOnly === true) {
            reduxDispatch(getFellowUsers(token))
          }
          dispatch({
            type: 'LOGIN',
            payload: {
              user: {
                auth0Token: token,
                id: user.sub,
                avatar: user.picture,
                email: user.email,
                name: `${user.given_name} ${user.family_name}`,
                ...data.data.user
              },
              tokens,
              permissions,
              numbers,
              account,
              statusMessages
            }
          })

          reduxDispatch(
            setContactSettings(account?.contactsData?.contactSettingsTemplates)
          )
        }
      } catch (error) {
        console.log(error)
        console.log(error.response)
        logout()
      }
    }
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        platform: 'Auth0',
        loginWithPopup,
        logout
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired
}

export default AuthContext
